import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Check, User, Plane, FileText, 
  Send, Phone, Mail, Calendar, Users, AlertCircle, Shield,
  CheckCircle, Clock
} from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './VisaApplicationForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Your Details', icon: User },
  { id: 2, title: 'Travel Info', icon: Plane },
  { id: 3, title: 'Documents', icon: FileText },
  { id: 4, title: 'Review', icon: Send },
];

const VisaApplicationForm = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Step 1: Personal Details
    name: '',
    email: '',
    phone: '',
    // Step 2: Travel Info
    travel_date: '',
    num_applicants: 1,
    purpose: 'tourism',
    // Step 3: Document confirmation
    documents_ready: false,
    has_passport: false,
    passport_validity: '',
    // Additional
    message: '',
  });

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/visa-products/by-slug/${slug}`);
      setProduct(res.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      navigate('/apply-online');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      else if (formData.phone.replace(/\D/g, '').length < 10) newErrors.phone = 'Invalid phone number';
    }
    
    if (step === 2) {
      if (!formData.travel_date) newErrors.travel_date = 'Travel date is required';
      if (formData.num_applicants < 1) newErrors.num_applicants = 'At least 1 applicant required';
    }
    
    if (step === 3) {
      if (!formData.has_passport) newErrors.has_passport = 'Please confirm you have a valid passport';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setSubmitting(true);
    
    try {
      // Create lead with "applied_online" source (use code, not name)
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: 'applied_online',
        service_type: product.visa_type,
        destination_country: product.country_name,
        travel_date: formData.travel_date,
        num_applicants: parseInt(formData.num_applicants, 10),
        message: `Purpose: ${formData.purpose}\nPassport validity confirmed: ${formData.has_passport ? 'Yes' : 'No'}\n${formData.message}`,
        temperature: 'hot', // Self-serve leads are hot
      };
      
      // Create lead as "New" first
      const response = await axios.post(`${BACKEND_URL}/api/leads`, leadData);
      
      // Then update to "Qualified" using public endpoint for Apply Online leads
      if (response.data?.id) {
        try {
          await axios.post(`${BACKEND_URL}/api/visa-products/qualify-lead/${response.data.id}`);
        } catch (err) {
          // Stage update failed, but lead was created - still consider success
          console.warn('Stage update failed:', err);
        }
      }
      
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting application:', error);
      setErrors({ submit: error.response?.data?.detail || 'Failed to submit. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="visa-form-page">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) return null;

  // Success Screen
  if (submitted) {
    return (
      <div className="visa-form-page">
        <Header />
        <div className="form-success-container">
          <div className="success-card">
            <div className="success-icon">
              <CheckCircle size={48} />
            </div>
            <h1>Application Submitted!</h1>
            <p className="success-subtitle">
              Thank you for your {product.country_name} visa application.
              Our team will contact you shortly.
            </p>
            
            <div className="success-info">
              <div className="success-info-row">
                <span>Reference</span>
                <strong>#{Date.now().toString(36).toUpperCase()}</strong>
              </div>
              <div className="success-info-row">
                <span>Destination</span>
                <strong>{product.country_name}</strong>
              </div>
              <div className="success-info-row">
                <span>Visa Type</span>
                <strong>{product.visa_type}</strong>
              </div>
            </div>
            
            <div className="success-next-steps">
              <h3>What's Next?</h3>
              <div className="next-step-item">
                <Clock size={18} />
                <span>Our consultant will call you within 24 hours</span>
              </div>
              <div className="next-step-item">
                <FileText size={18} />
                <span>Keep your documents ready for verification</span>
              </div>
              <div className="next-step-item">
                <Shield size={18} />
                <span>We'll guide you through the entire process</span>
              </div>
            </div>
            
            <button 
              className="btn-back-home"
              onClick={() => navigate('/apply-online')}
            >
              Browse More Destinations
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="visa-form-page">
      <Header />
      
      <div className="form-container">
        {/* Progress Header */}
        <div className="form-header">
          <button className="back-link" onClick={() => navigate(`/apply/${slug}`)}>
            <ArrowLeft size={18} />
            <span>Back to {product.country_name}</span>
          </button>
          
          <div className="form-title">
            <img src={product.flag_url} alt="" className="form-flag" />
            <div>
              <h1>{product.country_name} Visa Application</h1>
              <p>{product.visa_type}</p>
            </div>
          </div>
        </div>
        
        {/* Step Progress */}
        <div className="step-progress">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className={`step-item ${currentStep >= step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}>
                <div className="step-circle">
                  {currentStep > step.id ? <Check size={16} /> : <step.icon size={16} />}
                </div>
                <span className="step-label">{step.title}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`step-connector ${currentStep > step.id ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Content */}
        <div className="form-content">
          {/* Step 1: Personal Details */}
          {currentStep === 1 && (
            <div className="form-step">
              <h2>Your Personal Details</h2>
              <p className="step-description">Please provide your contact information</p>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name (as per passport) *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>
                
                <div className="form-group">
                  <label>Email Address *</label>
                  <div className="input-with-icon">
                    <Mail size={18} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className={errors.email ? 'error' : ''}
                    />
                  </div>
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
                
                <div className="form-group">
                  <label>Phone Number *</label>
                  <div className="input-with-icon">
                    <Phone size={18} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 98765 43210"
                      className={errors.phone ? 'error' : ''}
                    />
                  </div>
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Travel Info */}
          {currentStep === 2 && (
            <div className="form-step">
              <h2>Travel Information</h2>
              <p className="step-description">Tell us about your travel plans</p>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Planned Travel Date *</label>
                  <div className="input-with-icon">
                    <Calendar size={18} />
                    <input
                      type="date"
                      name="travel_date"
                      value={formData.travel_date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={errors.travel_date ? 'error' : ''}
                    />
                  </div>
                  {errors.travel_date && <span className="error-text">{errors.travel_date}</span>}
                </div>
                
                <div className="form-group">
                  <label>Number of Applicants *</label>
                  <div className="input-with-icon">
                    <Users size={18} />
                    <input
                      type="number"
                      name="num_applicants"
                      value={formData.num_applicants}
                      onChange={handleInputChange}
                      min="1"
                      max="20"
                      className={errors.num_applicants ? 'error' : ''}
                    />
                  </div>
                  {errors.num_applicants && <span className="error-text">{errors.num_applicants}</span>}
                </div>
                
                <div className="form-group full-width">
                  <label>Purpose of Visit</label>
                  <select
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                  >
                    <option value="tourism">Tourism / Leisure</option>
                    <option value="business">Business</option>
                    <option value="family">Family Visit</option>
                    <option value="medical">Medical Treatment</option>
                    <option value="education">Short Course / Training</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Documents */}
          {currentStep === 3 && (
            <div className="form-step">
              <h2>Document Checklist</h2>
              <p className="step-description">Confirm you have the required documents ready</p>
              
              <div className="document-checklist">
                {product.documents_checklist?.map((doc, index) => (
                  <div key={index} className="checklist-item">
                    <CheckCircle size={18} />
                    <span>{doc}</span>
                  </div>
                ))}
              </div>
              
              <div className="form-group checkbox-group">
                <label className={`checkbox-label ${errors.has_passport ? 'error' : ''}`}>
                  <input
                    type="checkbox"
                    name="has_passport"
                    checked={formData.has_passport}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span>I have a valid passport (minimum 6 months validity)</span>
                </label>
                {errors.has_passport && <span className="error-text">{errors.has_passport}</span>}
              </div>
              
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="documents_ready"
                    checked={formData.documents_ready}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span>I understand I need to prepare the above documents</span>
                </label>
              </div>
              
              <div className="form-group full-width">
                <label>Additional Notes (Optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Any special requirements or questions..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="form-step">
              <h2>Review Your Application</h2>
              <p className="step-description">Please verify your information before submitting</p>
              
              <div className="review-section">
                <h3>Personal Details</h3>
                <div className="review-grid">
                  <div className="review-item">
                    <span className="review-label">Full Name</span>
                    <span className="review-value">{formData.name}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Email</span>
                    <span className="review-value">{formData.email}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Phone</span>
                    <span className="review-value">{formData.phone}</span>
                  </div>
                </div>
              </div>
              
              <div className="review-section">
                <h3>Travel Information</h3>
                <div className="review-grid">
                  <div className="review-item">
                    <span className="review-label">Destination</span>
                    <span className="review-value">{product.country_name}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Visa Type</span>
                    <span className="review-value">{product.visa_type}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Travel Date</span>
                    <span className="review-value">{new Date(formData.travel_date).toLocaleDateString()}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Applicants</span>
                    <span className="review-value">{formData.num_applicants}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Purpose</span>
                    <span className="review-value" style={{textTransform: 'capitalize'}}>{formData.purpose}</span>
                  </div>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="form-disclaimer">
                <AlertCircle size={18} />
                <p>
                  <strong>Important:</strong> {product.embassy_disclaimer}
                </p>
              </div>
              
              {errors.submit && (
                <div className="submit-error">
                  <AlertCircle size={18} />
                  <span>{errors.submit}</span>
                </div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            {currentStep > 1 && (
              <button className="btn-secondary" onClick={prevStep}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
            )}
            
            {currentStep < 4 ? (
              <button className="btn-primary" onClick={nextStep}>
                <span>Continue</span>
                <ArrowRight size={18} />
              </button>
            ) : (
              <button 
                className="btn-submit" 
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <span>Submitting...</span>
                ) : (
                  <>
                    <span>Ready to Apply</span>
                    <Send size={18} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default VisaApplicationForm;
